import React from "react"
import {
	ATELIER_ETAT_CLOS,
	ATELIER_ETAT_TRAVAUX_TERMINES,
} from "../../utilities/Addons"
import { COLORS } from "../../utilities/Constants"

// Statuts d'échéance calculés par EntretienCycle.statut() (backend), par
// semaine calendaire. `fait` : cycles clos (calendrier, historique).
// `a_finaliser` (Jordan 27.10) : fiche en Travaux terminés, reste à clore —
// ni rouge ni vert, hors du badge « en retard ».
export const STATUTS = {
	retard: {
		label: "En retard",
		color: COLORS.danger,
		bg: COLORS.dangerLight,
	},
	du: {
		label: "Cette semaine",
		color: COLORS.warning,
		bg: COLORS.warningLight,
	},
	a_finaliser: {
		label: "À finaliser",
		color: COLORS.accent,
		bg: COLORS.accentLight,
	},
	a_venir: {
		label: "À venir",
		color: COLORS.success,
		bg: COLORS.successLight,
	},
	fait: { label: "Fait", color: COLORS.textMuted, bg: COLORS.bg },
}
export const STATUT_KEYS = ["retard", "du", "a_finaliser", "a_venir"]

// ISO (yyyy-mm-dd) → dd.mm.yyyy ; `court` → dd.mm
export const fmtDate = (iso, court = false) => {
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "")
	if (!m) return ""
	return court ? `${m[3]}.${m[2]}` : `${m[3]}.${m[2]}.${m[1]}`
}

export const fmtKm = (km) => `${Number(km || 0).toLocaleString("fr-CH")} km`

// « Dans 12 j · 8 300 km restants » — date et/ou km de l'échéance ouverte.
// Date passée : « En retard de N j » seulement au statut En retard ; au statut
// « Cette semaine » (`du`, la semaine en cours n'est pas un retard), « Prévu
// il y a N j ».
export const echeanceDetail = (e) => {
	const parts = []
	const j = e.jours_restants
	if (j !== null && j !== undefined) {
		if (j === 0) parts.push("Aujourd'hui")
		else if (j === 1) parts.push("Demain")
		else if (j === -1) parts.push("Hier")
		else if (j < 0)
			parts.push(
				e.statut === "retard"
					? `En retard de ${-j} j`
					: `Prévu il y a ${-j} j`,
			)
		else parts.push(`Dans ${j} j`)
	}
	const km = e.km_restants
	if (km !== null && km !== undefined) {
		if (km < 0) parts.push(`${fmtKm(-km)} dépassés`)
		else parts.push(`${fmtKm(km)} restants`)
	}
	return parts.join(" · ")
}

// Libellé principal de l'échéance : date, sinon seuil km.
export const echeanceTitre = (e) =>
	e.date
		? fmtDate(e.date)
		: e.km_cible !== null && e.km_cible !== undefined
			? fmtKm(e.km_cible)
			: "—"

// Échéance suivante, telle que créée à la clôture de la fiche
// (backend : creer_echeance_suivante) — date prévue + période, km de
// clôture + intervalle.
export const suivanteTxt = (e) => {
	const s = e.suivante
	if (!s) return null
	const parts = []
	if (s.date)
		parts.push(
			`${fmtDate(s.date)} = ${fmtDate(e.date, true)} + ${s.periode}`,
		)
	else if (s.periode) parts.push(`date de clôture + ${s.periode}`)
	if (s.cycle_km) parts.push(`km à la clôture + ${fmtKm(s.cycle_km)}`)
	return parts.join(" et ") || null
}

// Teinte de la pastille d'état d'une fiche atelier.
export const ficheTone = (etat) => {
	if (etat === ATELIER_ETAT_CLOS)
		return { color: COLORS.success, bg: COLORS.successLight }
	if (etat === ATELIER_ETAT_TRAVAUX_TERMINES)
		return { color: COLORS.accent, bg: COLORS.accentLight }
	return { color: COLORS.textMuted, bg: COLORS.bg }
}

// Le CSS global legacy (App.css) cible `th, td` (bordure grise 4 côtés,
// centré, 14px) et `tbody tr` (zébrage gris, survol gris foncé) : chaque
// propriété est donc redéfinie ici, en longhands (pas de mélange avec
// `border` → pas de conflit React au re-render).
const sansBordure = {
	borderTop: "none",
	borderLeft: "none",
	borderRight: "none",
	borderBottom: `1px solid ${COLORS.border}`,
}

// Styles partagés par les vues de /entretiens (table, pastilles).
export const tableStyles = {
	card: {
		backgroundColor: COLORS.surface,
		borderRadius: 16,
		border: `1px solid ${COLORS.border}`,
		overflow: "hidden",
	},
	scroll: { overflowX: "auto" },
	table: {
		width: "100%",
		borderCollapse: "collapse",
		fontSize: 13,
		minWidth: 900,
	},
	th: {
		padding: "10px 14px",
		textAlign: "left",
		fontSize: 11,
		fontWeight: 700,
		color: COLORS.textLight,
		textTransform: "uppercase",
		letterSpacing: "0.05em",
		...sansBordure,
		backgroundColor: COLORS.bg,
		whiteSpace: "nowrap",
	},
	td: {
		padding: "12px 14px",
		textAlign: "left",
		fontSize: 13,
		color: COLORS.text,
		...sansBordure,
		verticalAlign: "middle",
		fontVariantNumeric: "tabular-nums",
	},
	// Neutralise le zébrage / survol legacy (surchargé par les lignes cliquables).
	tr: { backgroundColor: "transparent", cursor: "default" },
	main: { fontWeight: 600 },
	sub: { fontSize: 12, color: COLORS.textMuted, marginTop: 2 },
	empty: {
		padding: "40px 16px",
		textAlign: "center",
		fontSize: 13,
		color: COLORS.textLight,
		...sansBordure,
		borderBottom: "none",
	},
	tag: {
		fontSize: 10,
		fontWeight: 700,
		textTransform: "uppercase",
		letterSpacing: "0.05em",
		color: COLORS.textMuted,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 5,
		padding: "1px 5px",
		marginLeft: 6,
		verticalAlign: 1,
	},
	link: {
		color: COLORS.accent,
		fontWeight: 600,
		fontSize: 12,
		cursor: "pointer",
		background: "none",
		border: "none",
		fontFamily: "inherit",
		padding: 0,
	},
	smallBtn: {
		padding: "5px 11px",
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.text,
		fontSize: 12,
		fontWeight: 600,
		cursor: "pointer",
		fontFamily: "inherit",
		whiteSpace: "nowrap",
	},
}

// Pastille pilule avec point de couleur.
export const pillStyle = ({ color, bg }) => ({
	display: "inline-flex",
	alignItems: "center",
	gap: 6,
	padding: "3px 10px 3px 8px",
	borderRadius: 999,
	fontSize: 11.5,
	fontWeight: 700,
	whiteSpace: "nowrap",
	color,
	backgroundColor: bg,
})
export const dotStyle = (color) => ({
	width: 7,
	height: 7,
	borderRadius: "50%",
	display: "inline-block",
	flex: "none",
	backgroundColor: color,
})

export const Pill = ({ tone, children }) => (
	<span style={pillStyle(tone)}>
		<span style={dotStyle(tone.color)} />
		{children}
	</span>
)
